import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Phone, Radio, Smartphone, MessageCircle } from "lucide-react";
import { WA_APPS, isAndroid, openWhatsApp } from "@/lib/whatsapp";

export default function CallMethodDialog({
  open,
  onOpenChange,
  phoneNumber,
  onChooseNative,
  onChooseDispatch,
  onChooseCallMyPhone,
  hasPhoneNumber,
}) {
  // "WhatsApp or WhatsApp Business?" row (Android only); folded each time the
  // dialog opens.
  const [waPick, setWaPick] = useState(false);
  useEffect(() => { if (open) setWaPick(false); }, [open]);

  const formatPhone = (number) => {
    if (!number) return "";
    const clean = number.replace(/\D/g, "");
    if (clean.length === 10) {
      return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
    if (clean.length === 11 && clean[0] === "1") {
      return `+1 (${clean.slice(1, 4)}) ${clean.slice(4, 7)}-${clean.slice(7)}`;
    }
    return number;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">Call {formatPhone(phoneNumber)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Button
            variant="outline"
            className="w-full h-14 justify-start gap-3 text-left"
            onClick={() => {
              onChooseNative();
              onOpenChange(false);
            }}
          >
            <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
              <Phone className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="font-medium">Native Dialer</div>
              <div className="text-xs text-slate-400">Use your phone's dialer app</div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="w-full h-14 justify-start gap-3 text-left"
            onClick={() => {
              onChooseDispatch();
              onOpenChange(false);
            }}
          >
            <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
              <Radio className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="font-medium">Dispatch Phone</div>
              <div className="text-xs text-slate-400">Use the dispatch phone</div>
            </div>
          </Button>

          {hasPhoneNumber && (
            <Button
              variant="outline"
              className="w-full h-14 justify-start gap-3 text-left"
              onClick={() => {
                onChooseCallMyPhone();
                onOpenChange(false);
              }}
            >
              <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="font-medium">Call My Phone</div>
                <div className="text-xs text-slate-400">Ring your cell, then bridge to destination</div>
              </div>
            </Button>
          )}

          {/* Opens the chat with this number in WhatsApp, from where the
              dispatcher can message or place a WhatsApp call. On Android it
              first asks which: WhatsApp or WhatsApp Business (lib/whatsapp.js). */}
          <Button
            variant="outline"
            className="w-full h-auto min-h-14 py-2 justify-start gap-3 text-left whitespace-normal"
            onClick={() => {
              if (isAndroid()) { setWaPick((v) => !v); return; }
              openWhatsApp(phoneNumber);
              onOpenChange(false);
            }}
          >
            <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="font-medium">WhatsApp</div>
              <div className="text-xs text-slate-400 break-words whitespace-normal">Open a WhatsApp chat to message or call (from your own WhatsApp)</div>
            </div>
          </Button>
          {waPick && (
            <div className="grid grid-cols-2 gap-2">
              {WA_APPS.map((a) => (
                <Button
                  key={a.key}
                  variant="outline"
                  className="h-11 text-sm border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/10"
                  onClick={() => { setWaPick(false); onOpenChange(false); openWhatsApp(phoneNumber, a.key); }}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
